import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { DocumentType } from '../types/Types';
import { AuthContext } from './AuthContext';
import { useToast } from './ToastContext';
import { useAllDocumentTypes } from '../hooks/useAllDocumentTypes';

// Translates a server error's messageKey (e.g. errors.documentTypes.statusInUse)
// with whatever interpolation fields the response body carries. Falls back to
// the raw message when the server didn't send a messageKey.
const translateApiError = (
  t: ReturnType<typeof useTranslation>['t'],
  err: any,
  fallback: string
): string => {
  const data = err?.response?.data;
  const messageKey: string | undefined = data?.messageKey;
  if (messageKey) {
    return t(messageKey, { statusName: data?.statusName, count: data?.count });
  }
  return data?.message || err?.message || fallback;
};

type StatusFieldItem = {
  _id?: string;
  code: string;
  name: string;
  affectsStock?: boolean;
  functions?: string;
  triggersGLPosting?: boolean;
};

type DocumentTypeUploadItem = {
  name: string;
  code: string;
  warehouse: string;
  warehouseTo?: string;
  category: string;
  subCategory?: string;
  subType?: string;
  menuGroup?: string;
  status?: boolean;
  statusFields?: StatusFieldItem[];
  materialAccounts?: Array<Record<string, any>>;
  stornoDocumentType?: string | null;
  povratnicaDocumentType?: string | null;
  receptionDocumentType?: string | null;
  importReceptionDocumentType?: string | null;
  salesInvoiceDocumentType?: string | null;
  inventorySurplusDocumentType?: string | null;
  inventoryShortageDocumentType?: string | null;
  buyerPlanDocumentType?: string | null;
  supplierPlanDocumentType?: string | null;
  copyFromTypes?: string[];
};

interface DocumentTypesContextType {
  documentTypes: DocumentType[];
  /** Unfiltered codebook for role and route lookups outside the /documents list. */
  allDocumentTypes: DocumentType[];
  /** True after the first all-document-types fetch finishes, including errors. */
  allDocumentTypesLoaded: boolean;
  loading: boolean;
  error: string | null;
  errorMessageKey: string | null;
  refreshDocumentTypes: () => Promise<void>;
  createDocumentType: (data: DocumentTypeUploadItem) => Promise<void>;
  updateDocumentType: (id: string, data: Partial<DocumentType>) => Promise<void>;
  deleteDocumentType: (id: string) => Promise<boolean>;
  bulkUploadDocumentTypes: (items: DocumentTypeUploadItem[]) => Promise<void>;
  downloadDocumentTypesExcel: (language?: string) => Promise<void>;
  uploadDocumentTypesExcel: (file: File) => Promise<any>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (order: 'asc' | 'desc') => void;
}

const DocumentTypesContext = createContext<DocumentTypesContextType | null>(null);

interface DocumentTypesProviderProps {
  children: ReactNode;
}

export const DocumentTypesProvider: React.FC<DocumentTypesProviderProps> = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorMessageKey, setErrorMessageKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const apiUrl = process.env.REACT_APP_API_URL ?? "";
  const baseUrl = `${apiUrl}/api/documentsType`;
  const {
    allDocumentTypes,
    allDocumentTypesLoaded,
    refreshAllDocumentTypes,
    setAllDocumentTypes,
    setAllDocumentTypesLoaded,
  } = useAllDocumentTypes(baseUrl);

  const refreshDocumentTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      const response = await axios.get<DocumentType[]>(baseUrl, {
        headers: { "x-auth-token": token },
        params: {
          search: searchQuery,
          sort: sortBy,
          order: sortOrder,
        },
      });

      setDocumentTypes(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to fetch document types");
    } finally {
      setLoading(false);
    }
  };

  const createDocumentType = async (data: DocumentTypeUploadItem) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      await axios.post(baseUrl, data, {
        headers: {
          "x-auth-token": token,
          "Content-Type": "application/json",
        },
      });

      await Promise.all([refreshDocumentTypes(), refreshAllDocumentTypes()]);
    } catch (err: any) {
      const message = translateApiError(t, err, "Failed to create document type");
      showToast(message, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateDocumentType = async (
    id: string,
    data: Partial<DocumentType>
  ) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      // Extract warehouse ID if it's a populated object
      const cleanedData: any = {
        ...data,
        warehouse: typeof data.warehouse === 'object' && data.warehouse !== null
          ? data.warehouse._id
          : data.warehouse
      };

      // Extract warehouseTo ID if it's a populated object
      if (data.warehouseTo !== undefined) {
        cleanedData.warehouseTo = typeof data.warehouseTo === 'object' && data.warehouseTo !== null
          ? data.warehouseTo._id
          : data.warehouseTo;
      }

      if (data.stornoDocumentType !== undefined) {
        cleanedData.stornoDocumentType = typeof data.stornoDocumentType === 'object' && data.stornoDocumentType !== null
          ? data.stornoDocumentType._id
          : data.stornoDocumentType;
      }

      if (data.povratnicaDocumentType !== undefined) {
        cleanedData.povratnicaDocumentType = typeof data.povratnicaDocumentType === 'object' && data.povratnicaDocumentType !== null
          ? data.povratnicaDocumentType._id
          : data.povratnicaDocumentType;
      }

      if (data.receptionDocumentType !== undefined) {
        cleanedData.receptionDocumentType = typeof data.receptionDocumentType === 'object' && data.receptionDocumentType !== null
          ? data.receptionDocumentType._id
          : data.receptionDocumentType;
      }

      if (data.importReceptionDocumentType !== undefined) {
        cleanedData.importReceptionDocumentType = typeof data.importReceptionDocumentType === 'object' && data.importReceptionDocumentType !== null
          ? data.importReceptionDocumentType._id
          : data.importReceptionDocumentType;
      }

      for (const field of [
        'salesInvoiceDocumentType',
        'inventorySurplusDocumentType',
        'inventoryShortageDocumentType',
      ] as const) {
        const selectedType = data[field];
        if (selectedType !== undefined) {
          cleanedData[field] = typeof selectedType === 'object' && selectedType !== null
            ? selectedType._id
            : selectedType;
        }
      }

      if (data.buyerPlanDocumentType !== undefined) {
        cleanedData.buyerPlanDocumentType = typeof data.buyerPlanDocumentType === 'object' && data.buyerPlanDocumentType !== null
          ? data.buyerPlanDocumentType._id
          : data.buyerPlanDocumentType;
      }

      if (data.supplierPlanDocumentType !== undefined) {
        cleanedData.supplierPlanDocumentType = typeof data.supplierPlanDocumentType === 'object' && data.supplierPlanDocumentType !== null
          ? data.supplierPlanDocumentType._id
          : data.supplierPlanDocumentType;
      }

      await axios.put(`${baseUrl}/${id}`, cleanedData, {
        headers: {
          "x-auth-token": token,
          "Content-Type": "application/json",
        },
      });

      await Promise.all([refreshDocumentTypes(), refreshAllDocumentTypes()]);
    } catch (err: any) {
      const message = translateApiError(t, err, "Failed to update document type");
      showToast(message, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteDocumentType = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      await axios.delete(`${baseUrl}/${id}`, {
        headers: { "x-auth-token": token },
      });

      await Promise.all([refreshDocumentTypes(), refreshAllDocumentTypes()]);
      return true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || "Failed to delete document type";
      showToast(message, 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const bulkUploadDocumentTypes = async (items: DocumentTypeUploadItem[]) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing");

      const validItems = items.filter((item) => item.name && item.code && item.category);

      if (validItems.length === 0) {
        throw new Error("No valid document type items to upload.");
      }

      const response = await axios.post(
        `${baseUrl}/bulk`,
        { items: validItems },
        {
          headers: {
            "x-auth-token": token,
            "Content-Type": "application/json",
          },
        }
      );

      await Promise.all([refreshDocumentTypes(), refreshAllDocumentTypes()]);
    } catch (err: any) {
      let errorMessage = "Failed to upload document types";

      if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
        errorMessage = `Validation errors:\n${err.response.data.errors.join('\n')}`;
      } else if (err.response?.data?.existingCodes) {
        errorMessage = `Some codes already exist: ${err.response.data.existingCodes.join(', ')}`;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      showToast(errorMessage, 'error');
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Excel download for document types with multilingual support
  const downloadDocumentTypesExcel = async (language: string = 'en') => {
    setLoading(true);
    setError(null);
    setErrorMessageKey(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const response = await axios.get(`${apiUrl}/api/documentsType/excel`, {
        headers: { 'x-auth-token': token },
        params: {
          search: searchQuery,
          lang: language
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
      );

      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `document-types_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to download document types Excel';
      showToast(message, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Excel upload for document types
  const uploadDocumentTypesExcel = async (file: File) => {
    setLoading(true);
    setError(null);
    setErrorMessageKey(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token missing');

      const formData = new FormData();
      formData.append('excel', file);

      const response = await axios.post(`${apiUrl}/api/documentsType/import/excel`, formData, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data'
        }
      });

      await Promise.all([refreshDocumentTypes(), refreshAllDocumentTypes()]);
      return response.data;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to upload document types Excel';
      showToast(message, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Load document types when user logs in and when search/sort parameters change
  // Skip fetch if no user is authenticated (no token available)
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    let retryCount = 0;
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    const fetchWithRetry = async () => {
      try {
        await refreshDocumentTypes();
      } catch {
        if (!cancelled && retryCount < 3) {
          retryCount++;
          const timeout = setTimeout(fetchWithRetry, 2000 * retryCount);
          timeouts.push(timeout);
        }
      }
    };

    fetchWithRetry();

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, [user, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    if (!user) {
      setAllDocumentTypes([]);
      setAllDocumentTypesLoaded(false);
      return;
    }
    void refreshAllDocumentTypes();
  }, [refreshAllDocumentTypes, user]);

  const value: DocumentTypesContextType = {
    documentTypes,
    allDocumentTypes,
    allDocumentTypesLoaded,
    loading,
    error,
    errorMessageKey,
    refreshDocumentTypes,
    createDocumentType,
    updateDocumentType,
    deleteDocumentType,
    bulkUploadDocumentTypes,
    downloadDocumentTypesExcel,
    uploadDocumentTypesExcel,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
  };

  return (
    <DocumentTypesContext.Provider value={value}>
      {children}
    </DocumentTypesContext.Provider>
  );
};

export const useDocumentTypesContext = (): DocumentTypesContextType => {
  const context = useContext(DocumentTypesContext);
  if (!context) {
    throw new Error('useDocumentTypesContext must be used within a DocumentTypesProvider');
  }
  return context;
};

export default DocumentTypesContext;
